import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Brand } from "../components/Brand";
import { formatApiError } from "../lib/api";
import { Loader2, ArrowRight } from "lucide-react";

const ROLE_HOME = {
  owner: "/app",
  office: "/app/operations",
  builder: "/builder",
  field: "/field",
  homeowner: "/portal",
};

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email, password);
      navigate(ROLE_HOME[u.role] || "/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!loading && user) return <Navigate to={ROLE_HOME[user.role] || "/app"} replace />;

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Visual side */}
      <div className="relative hidden overflow-hidden lg:block">
        <img
          src="/media/illustrations/commercial.webp"
          alt="Commercial electrical environment"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#070B14] via-[#070B14]/80 to-cyan-950/40" />
        <div className="absolute inset-0 grid-texture opacity-40" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Brand size={42} subtitle />
          <div className="max-w-md">
            <div className="hw-eyebrow mb-4">Operating System for Electrical Contractors</div>
            <h1 className="font-head text-5xl font-black uppercase leading-none tracking-tight text-white">
              One platform.<br />
              <span className="text-cyan-400">Every crew. Every lot.</span><br />
              Every dollar.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-300">
              From the owner's command center to the field crew's phone — Hardwire connects the
              complicated stuff behind the scenes, so each person sees only what they need.
            </p>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center bg-[#070B14] px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <Brand size={40} />
          </div>
          <div className="hw-eyebrow mb-2">Sign in</div>
          <h2 className="font-head text-4xl font-extrabold text-white">Welcome back</h2>
          <p className="mt-2 text-slate-400">Access your Hardwire OS workspace.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label htmlFor="login-email" className="mb-1.5 block text-sm font-medium text-slate-300">Email</label>
              <input
                id="login-email"
                required
                className="hw-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                autoComplete="username"
              />
            </div>
            <div>
              <label htmlFor="login-password" className="mb-1.5 block text-sm font-medium text-slate-300">Password</label>
              <input
                id="login-password"
                required
                className="hw-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                autoComplete="current-password"
              />
            </div>
            {error && (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 py-3.5 font-bold text-slate-900 transition hover:bg-cyan-400 disabled:opacity-60"
              data-testid="login-submit"
            >
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <>Sign in <ArrowRight size={18} /></>}
            </button>
          </form>

          <div className="mt-8 flex flex-col gap-4 text-sm">
            <Link to="/register" className="font-semibold text-cyan-400" data-testid="login-register-link">New customer or builder? Create an account</Link>
            <Link to="/forgot-password" className="text-slate-300" data-testid="login-reset-link">Reset password</Link>
            <Link to="/" className="text-slate-400" data-testid="login-home-link">Back to the website</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
